import React, { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { styleSizesNumber } from 'constants/styleSizes';
import { HintTooltip, HintTrigger } from './style';

type InteractiveHintProps = {
  title?: ReactNode;
  content: ReactNode;
  children: ReactNode;
};

type Point = { x: number; y: number };
type PointerAnchor = {
  clientWidth: number;
  clientHeight: number;
  referenceNode?: HTMLElement;
  getBoundingClientRect: () => DOMRect;
};

// Shared by all instances, including hints mounted in different components.
let closeActiveHint: (() => void) | null = null;
let nextHintId = 0;

const InteractiveHint: React.FC<InteractiveHintProps> = ({ title, content, children }) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pointRef = useRef<Point | null>(null);
  const pointerFocusRef = useRef(false);
  const updatePositionRef = useRef<(() => void) | null>(null);
  const pointerAnchor = useRef<PointerAnchor>({
    clientWidth: 0,
    clientHeight: 0,
    getBoundingClientRect: () => {
      const point = pointRef.current;
      return point ? new DOMRect(point.x, point.y, 0, 0) : new DOMRect();
    },
  });
  const [anchor, setAnchor] = useState<HTMLSpanElement | PointerAnchor | null>(null);
  const [id] = useState(() => `interactive-hint-${++nextHintId}`);

  const close = useCallback(() => {
    if (closeActiveHint === close) closeActiveHint = null;
    setAnchor(null);
  }, []);

  const show = (point: Point | null = null) => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    if (closeActiveHint && closeActiveHint !== close) closeActiveHint();
    closeActiveHint = close;
    pointRef.current = point;
    pointerAnchor.current.referenceNode = trigger;
    setAnchor(point ? pointerAnchor.current : trigger);
    updatePositionRef.current?.();
  };

  const onBlur = (event: React.FocusEvent) => {
    const next = event.relatedTarget;
    if (next instanceof Node && (triggerRef.current?.contains(next) || contentRef.current?.contains(next))) return;
    pointerFocusRef.current = false;
    close();
  };

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    // Use the child's native tab stop when present; icons and text need the wrapper.
    const focusable = trigger.querySelector<HTMLElement>(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), ' +
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), ' +
      '[tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
    );
    trigger.tabIndex = focusable ? -1 : 0;
  }, [children]);

  useLayoutEffect(() => () => {
    if (closeActiveHint === close) closeActiveHint = null;
  }, [close]);

  useLayoutEffect(() => {
    if (!anchor) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    let closeTimer: number | undefined;

    const onClick = (event: MouseEvent) => {
      if (event.target instanceof Node && trigger.contains(event.target)) return;
      // Let the clicked element finish its own action before removing the popup.
      // Switching triggers closes this hint in show(); this callback cannot close the new owner.
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (closeActiveHint === close) close();
      }, 0);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const checkTrigger = () => {
      const rect = trigger.getBoundingClientRect();
      if (!trigger.isConnected || !rect.width || !rect.height ||
          trigger.closest('[hidden], [inert], [aria-hidden="true"]') ||
          window.getComputedStyle(trigger).visibility === 'hidden') {
        close();
      }
    };
    const observer = new MutationObserver(checkTrigger);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'inert', 'aria-hidden', 'style', 'class'],
    });
    const resizeObserver = new ResizeObserver(checkTrigger);
    resizeObserver.observe(trigger);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeyDown, true);
    // A point anchor should not remain behind when its trigger moves or becomes hidden.
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.visualViewport?.addEventListener('resize', close);
    window.visualViewport?.addEventListener('scroll', close);
    const focused = document.activeElement;
    const described = focused instanceof HTMLElement && trigger.contains(focused) ? focused : trigger;
    const descriptions = (described.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    const addedDescription = !descriptions.includes(id);
    if (addedDescription) described.setAttribute('aria-describedby', [...descriptions, id].join(' '));
    checkTrigger();

    return () => {
      window.clearTimeout(closeTimer);
      observer.disconnect();
      resizeObserver.disconnect();
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.visualViewport?.removeEventListener('resize', close);
      window.visualViewport?.removeEventListener('scroll', close);
      if (addedDescription) {
        const remaining = (described.getAttribute('aria-describedby') || '').split(/\s+/)
          .filter(description => description && description !== id);
        if (remaining.length) described.setAttribute('aria-describedby', remaining.join(' '));
        else described.removeAttribute('aria-describedby');
      }
    };
  }, [anchor, close, id]);

  return (
    <HintTooltip
      id={id}
      open={Boolean(anchor)}
      title={
        <div ref={contentRef} onBlur={onBlur}>
          {title != null && <div className="hintTitle">{title}</div>}
          <div className="hintContent">{content}</div>
        </div>
      }
      disableHoverListener
      disableFocusListener
      disableTouchListener
      interactive
      TransitionProps={{ timeout: 0 }}
      PopperProps={{
        anchorEl: anchor || triggerRef.current,
        disablePortal: false,
        // Hide synchronously, including while Material UI finishes its exit lifecycle.
        style: { visibility: anchor ? 'visible' : 'hidden' },
        popperRef: (instance: { scheduleUpdate: () => void } | null) => {
          updatePositionRef.current = instance ? () => instance.scheduleUpdate() : null;
        },
      }}
    >
      <HintTrigger
        ref={triggerRef}
        tabIndex={0}
        onMouseEnter={(event) => {
          if (window.innerWidth > styleSizesNumber.tabletBreakpoint) {
            show({ x: event.clientX, y: event.clientY });
          }
        }}
        onMouseMove={(event) => {
          if (window.innerWidth > styleSizesNumber.tabletBreakpoint && closeActiveHint === close) {
            pointRef.current = { x: event.clientX, y: event.clientY };
            if (anchor !== pointerAnchor.current) setAnchor(pointerAnchor.current);
            updatePositionRef.current?.();
          }
        }}
        onMouseLeave={() => {
          if (window.innerWidth > styleSizesNumber.tabletBreakpoint) close();
        }}
        onPointerDownCapture={() => { pointerFocusRef.current = true; }}
        onPointerCancelCapture={() => { pointerFocusRef.current = false; }}
        onClickCapture={(event) => {
          pointerFocusRef.current = false;
          if (window.innerWidth <= styleSizesNumber.tabletBreakpoint && event.detail !== 0) {
            if (closeActiveHint === close) close();
            else show({ x: event.clientX, y: event.clientY });
          }
        }}
        onKeyDownCapture={() => { pointerFocusRef.current = false; }}
        onFocusCapture={() => {
          // Pointer focus must not open a hint just before the same tap toggles it.
          if (!pointerFocusRef.current) show();
        }}
        onBlurCapture={onBlur}
      >
        {children}
      </HintTrigger>
    </HintTooltip>
  );
};

export default InteractiveHint;
